import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { api } from '../lib/api';

/**
 * Org Features Context — loads /api/org/features once per session and
 * exposes flags + limits to the whole app for UI gating.
 */
const OrgFeaturesContext = createContext({
  loading: true,
  features: {},
  limits: {},
  planKey: null,
  planName: null,
  isAdmin: false,
  subscriptionStatus: 'trial',
  refresh: () => {},
  hasFeature: () => false,
});

export function OrgFeaturesProvider({ children }) {
  const { organization } = useOrganization();
  const [state, setState] = useState({
    loading: true,
    features: {},
    limits: {},
    planKey: null,
    planName: null,
    isAdmin: false,
    subscriptionStatus: 'trial',
  });

  const load = useCallback(async () => {
    if (!organization?.id) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    try {
      const { data } = await api.get('/api/org/features');
      setState({
        loading: false,
        features: data.features || {},
        limits: data.limits || {},
        planKey: data.plan_key || null,
        planName: data.plan_name || null,
        isAdmin: !!data.is_admin,
        subscriptionStatus: data.subscription_status || 'trial',
      });
    } catch (e) {
      console.warn('useOrgFeatures: failed to load', e);
      setState((s) => ({ ...s, loading: false }));
    }
  }, [organization?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const hasFeature = useCallback(
    (key) => {
      if (state.isAdmin) return true; // super_admins see everything
      return !!state.features[key];
    },
    [state]
  );

  return (
    <OrgFeaturesContext.Provider value={{ ...state, refresh: load, hasFeature }}>
      {children}
    </OrgFeaturesContext.Provider>
  );
}

export function useOrgFeatures() {
  return useContext(OrgFeaturesContext);
}
